import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, API } from "../App";
import { Mail, ArrowRight, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import axios from "axios";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const email = location.state?.email || "";

  useEffect(() => {
    if (!email) navigate("/register", { replace: true });
  }, [email, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-email`, { email, code }, { withCredentials: true });
      if (res.data.token) {
        login(res.data);
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Error de verificacion");
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    setResent(false);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch {}
    finally { setResending(false); }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4 sm:p-8" data-testid="verify-email-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1.5">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-xl tracking-tight text-[#111111]">FUTURA</span>
          </Link>
          <LanguageSelector variant="light" />
        </div>

        <div className="animate-fade-in text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#002FA7]/10 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-[#002FA7]" />
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold text-[#111111] tracking-tight mb-2" data-testid="verify-title">
            {t("verify_title")}
          </h1>
          <p className="text-[#6B7280] mb-2 text-sm">{t("verify_subtitle")}</p>
          <p className="text-[#111111] font-medium mb-8">{email}</p>

          <form onSubmit={handleVerify} className="space-y-4 text-left">
            <div className="space-y-2">
              <Label className="text-[#111111] font-medium">{t("verify_code_label")}</Label>
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className="h-14 bg-white border-[#E5E5EA] rounded-xl text-center text-2xl font-bold tracking-[0.5em] focus:border-[#002FA7]"
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                data-testid="verify-code-input"
              />
            </div>

            {error && <p className="text-red-500 text-sm text-center" data-testid="verify-error">{error}</p>}

            <Button type="submit" disabled={loading || code.length !== 6} className="w-full h-12 bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-xl font-medium" data-testid="verify-submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("verify_btn")}<ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
          </form>

          <div className="mt-6">
            <button onClick={handleResend} disabled={resending} className="text-sm text-[#002FA7] hover:underline flex items-center gap-2 mx-auto" data-testid="resend-code-btn">
              {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {t("verify_resend")}
            </button>
            {resent && <p className="text-sm text-[#10B981] mt-2">{t("verify_resent")}</p>}
          </div>

          <p className="text-center mt-8 text-[#6B7280] text-sm">
            <Link to="/login" className="text-[#002FA7] hover:underline">{t("verify_back_login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
